import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowLeft, ImageOff, Download, Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { API, BACKEND_URL } from "@/lib/api";
import Lightbox from "@/components/Lightbox";

const VARIANT_STYLES = {
  dr: { accent: "#2997FF", label: "DEAR" },
  kl: { accent: "#00E676", label: "KL" },
  golden_raja: { accent: "#FFD700", label: "Golden Raja" },
};

const FEATURE_IMG =
  "https://images.unsplash.com/photo-1771226281605-f1e505ade901?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdvbGQlMjBnZW9tZXRyeSUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4NjI5NjYwMnww&ixlib=rb-4.1.0&q=85";

export default function ChartGallery() {
  const [summary, setSummary] = useState(null);
  const [latestResults, setLatestResults] = useState([]);
  const [variant, setVariant] = useState("dr");
  const [category, setCategory] = useState(null);
  const [charts, setCharts] = useState([]);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [chartsError, setChartsError] = useState(false);
  const [active, setActive] = useState(null);
  const [sharingAll, setSharingAll] = useState(false);

  const handleDownloadAll = (cat) => {
    toast.info(`Preparing ${cat.count ?? charts.length} charts ZIP… சிறிது நேரம் ஆகும்`);
    const a = document.createElement("a");
    a.href = `${API}/charts/zip?category_id=${cat.id}`;
    a.download = `${cat.name} charts.zip`;
    a.rel = "noopener";
    document.body.appendChild(a); // mobile Chrome needs the anchor in the DOM
    a.click();
    a.remove();
  };

  const handleShareAll = async (cat) => {
    setSharingAll(true);
    const waFallback = () => {
      const text = `${cat.name} — Rocky Agency\n` + charts
        .map((c) => `${c.title}\n${BACKEND_URL}/api/files/${c.url.replace("/api/files/", "")}`)
        .join("\n");
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    };
    try {
      const files = [];
      for (const c of charts) {
        const res = await fetch(`${API}/files/${c.url.replace("/api/files/", "")}`);
        if (!res.ok) throw new Error(`image fetch failed (${res.status})`);
        const blob = await res.blob();
        files.push(new File([blob], `${c.title}.png`, { type: "image/png" }));
      }
      if (navigator.canShare && navigator.canShare({ files })) {
        try {
          await navigator.share({ files, title: `${cat.name} — Rocky Agency` });
        } catch (e) {
          if (e?.name === "AbortError") {
            setSharingAll(false);
            return;
          }
          // device/browser rejected multi-file share (too many/large files) —
          // fall back to WhatsApp with direct image links instead of failing
          waFallback();
        }
      } else {
        waFallback();
      }
    } catch (e) {
      toast.error("Share failed — internet slow ஆக இருக்கலாம், மீண்டும் முயற்சிக்கவும்");
    }
    setSharingAll(false);
  };

  useEffect(() => {
    const loadSummary = () => {
      api.get("/summary").then((res) => setSummary(res.data)).catch(() => {});
      api.get("/results/latest").then((res) => setLatestResults(res.data)).catch(() => {});
    };
    loadSummary();
    const t = setInterval(loadSummary, 180000);
    return () => clearInterval(t);
  }, []);

  const resultFor = (cat) => {
    if (variant === "golden_raja") return latestResults.find((r) => r.variant === "golden_raja");
    return latestResults.find((r) => r.variant !== "golden_raja" && r.category_name === cat.name);
  };

  useEffect(() => {
    if (!category) return;
    const load = (silent) => {
      if (!silent) setLoadingCharts(true);
      api
        .get(`/charts?category_id=${category.id}`)
        .then((res) => {
          setCharts(res.data);
          setChartsError(false);
        })
        .catch(() => setChartsError(true))
        .finally(() => setLoadingCharts(false));
    };
    load(false);
    const t = setInterval(() => load(true), 180000);
    return () => clearInterval(t);
  }, [category]);

  const current = summary?.variants.find((v) => v.key === variant);
  const accent = VARIANT_STYLES[variant]?.accent || "#FFD700";

  return (
    <section id="gallery" data-testid="chart-gallery" className="px-6 py-32 md:px-16 lg:px-24">
      <div className="mx-auto max-w-[1600px]">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
        >
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-[#666666]">The Vault</p>
          <h2 className="mb-14 font-heading text-4xl font-black uppercase tracking-tighter md:text-6xl">
            Browse the <span style={{ color: accent }}>charts</span>
          </h2>
        </motion.div>

        <div id="variants" className="mb-16 flex flex-wrap gap-3" data-testid="variant-selector">
          {Object.entries(VARIANT_STYLES).map(([key, style]) => {
            const total = summary?.variants.find((v) => v.key === key)?.total ?? 0;
            const isActive = variant === key;
            return (
              <button
                key={key}
                data-testid={`variant-selector-${key}`}
                onClick={() => {
                  setVariant(key);
                  setCategory(null);
                }}
                className="flex items-center gap-3 rounded-full border px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] transition-all duration-300"
                style={{
                  borderColor: isActive ? style.accent : "rgba(255,255,255,0.12)",
                  backgroundColor: isActive ? style.accent : "transparent",
                  color: isActive ? "#050505" : "#A1A1A6",
                }}
              >
                {style.label}
                <span
                  className="rounded-full px-2 py-0.5 font-mono text-[10px]"
                  style={{
                    backgroundColor: isActive ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.08)",
                    color: isActive ? "#050505" : "#666666",
                  }}
                >
                  {total}
                </span>
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          {!category ? (
            <motion.div
              key={`cats-${variant}`}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
              className="grid grid-cols-1 gap-0 md:grid-cols-12 md:gap-6"
              data-testid="category-grid"
            >
              {current?.categories.map((cat, i) => {
                const isFeature = i === 0;
                const lr = resultFor(cat);
                return (
                  <button
                    key={cat.id}
                    data-testid={`category-card-${cat.id}`}
                    onClick={() => setCategory(cat)}
                    className={`group relative overflow-hidden border border-white/10 text-left transition-colors duration-300 hover:border-white/30 ${
                      isFeature ? "md:col-span-8 md:row-span-2 min-h-[280px]" : "md:col-span-4 min-h-[140px]"
                    }`}
                  >
                    {isFeature && (
                      <img
                        src={FEATURE_IMG}
                        alt=""
                        className="absolute inset-0 h-full w-full object-cover opacity-20 transition-opacity duration-500 group-hover:opacity-35"
                      />
                    )}
                    <div className="relative z-10 flex h-full flex-col justify-between p-6 md:p-8">
                      <div className="flex flex-col items-start gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#666666]">
                          {String(cat.count).padStart(2, "0")} charts
                        </span>
                        {lr && (
                          <span
                            data-testid={`latest-result-${cat.id}`}
                            className="inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-xs font-bold tracking-[0.15em]"
                            style={{
                              color: accent,
                              borderColor: `${accent}55`,
                              backgroundColor: `${accent}14`,
                              textShadow: `0 0 16px ${accent}88`,
                            }}
                          >
                            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full" style={{ backgroundColor: accent }} />
                            {lr.time ? `${lr.time} · ` : ""}
                            {lr.result}
                          </span>
                        )}
                      </div>
                      <div className="flex items-end justify-between">
                        <h3
                          className={`font-heading font-bold uppercase tracking-tight ${
                            isFeature ? "text-3xl md:text-5xl" : "text-xl md:text-2xl"
                          }`}
                        >
                          {cat.name}
                        </h3>
                        <ArrowUpRight
                          className="h-5 w-5 shrink-0 text-[#666666] transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1"
                          style={{ color: undefined }}
                        />
                      </div>
                    </div>
                  </button>
                );
              })}
            </motion.div>
          ) : (
            <motion.div
              key={`charts-${category.id}`}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
              data-testid="charts-grid"
            >
              <button
                data-testid="back-to-categories"
                onClick={() => setCategory(null)}
                className="mb-10 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#A1A1A6] transition-colors duration-300 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" /> All {VARIANT_STYLES[variant].label} categories
              </button>
              <h3 className="mb-10 font-heading text-3xl font-black uppercase tracking-tight md:text-5xl">
                {category.name}
              </h3>

              {!loadingCharts && !chartsError && charts.length > 0 && (
                <div className="mb-10 flex flex-wrap items-center gap-3" data-testid="bulk-actions">
                  <button
                    data-testid="download-all-btn"
                    onClick={() => handleDownloadAll(category)}
                    className="flex items-center gap-2 rounded-full border px-6 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] transition-colors duration-300"
                    style={{ borderColor: `${accent}66`, color: accent, backgroundColor: `${accent}14` }}
                  >
                    <Download className="h-4 w-4" /> Download all ({charts.length})
                  </button>
                  <button
                    data-testid="share-all-btn"
                    onClick={() => handleShareAll(category)}
                    disabled={sharingAll}
                    className="flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white/10 disabled:opacity-50"
                  >
                    <Share2 className="h-4 w-4" /> {sharingAll ? "Preparing…" : `Share all (${charts.length})`}
                  </button>
                </div>
              )}

              {loadingCharts ? (
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="aspect-[3/4] animate-pulse rounded-lg bg-white/5" />
                  ))}
                </div>
              ) : chartsError ? (
                <div
                  data-testid="charts-error-state"
                  className="flex flex-col items-center gap-4 border border-dashed border-red-500/30 py-24 text-center"
                >
                  <ImageOff className="h-8 w-8 text-red-400" />
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#A1A1A6]">
                    Couldn&apos;t load charts — check your connection
                  </p>
                  <button
                    data-testid="charts-retry-button"
                    onClick={() => {
                      setChartsError(false);
                      setLoadingCharts(true);
                      api
                        .get(`/charts?category_id=${category.id}`)
                        .then((res) => setCharts(res.data))
                        .catch(() => setChartsError(true))
                        .finally(() => setLoadingCharts(false));
                    }}
                    className="border border-white/20 px-6 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white transition-colors hover:bg-white/10"
                  >
                    Retry
                  </button>
                </div>
              ) : charts.length === 0 ? (
                <div
                  data-testid="charts-empty-state"
                  className="flex flex-col items-center gap-4 border border-dashed border-white/15 py-24 text-center"
                >
                  <ImageOff className="h-8 w-8 text-[#666666]" />
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#666666]">
                    Charts dropping soon — admin uploads daily
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
                  {charts.map((chart, i) => (
                    <motion.button
                      key={chart.id}
                      data-testid={`chart-card-${chart.id}`}
                      initial={{ opacity: 0, y: 24 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: i * 0.05 }}
                      onClick={() => setActive(chart)}
                      className="group overflow-hidden rounded-lg border border-white/10 bg-[#0A0A0A] text-left transition-all duration-300 hover:-translate-y-1 hover:border-white/30"
                    >
                      <div className="aspect-[3/4] overflow-hidden bg-black">
                        <img
                          src={`${API}/files/${chart.url.replace("/api/files/", "")}`}
                          alt={chart.title}
                          loading="lazy"
                          className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4">
                        <p className="truncate text-sm font-semibold">{chart.title}</p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#666666]">
                          {new Date(chart.created_at).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                          })}
                        </p>
                      </div>
                    </motion.button>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {active && <Lightbox chart={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
}
